import { TabPanel, TabPanels, Tabs } from '@reach/tabs';
import { withExperiments } from '@wix/wix-experiments-react';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { withGtmLogger } from '../../components/HOCs/withGtmLogger';
import { RequestCallModal } from '../../components/RequestCall/Modal/RequestCallModal';
import { Experiments, PlanType, Theme } from 'enums';
import { withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { translateAllKeyInObject } from 'utils';
import { usePlansPageController } from './PlansPage.controller';
import { PlansPageProps } from './PlansPageProps';
import { LimitedTimeBanner } from './LimitedTimeBanner/LimitedTimeBanner';
import PlansGrid from './AdvancedPlans/PlansGrid';
import s from './PlansPage.scss';
import { TabButtons } from './Tabs/TabButtons';
import { TabPanelTransition } from './Tabs/TabPanelTransition';
import { EnterpriseTab } from './Enterprise/EnterpriseTab';
import { CustomersPaymentBanner, NoTabsBanner } from './PaymentBanners';
import { GridWrapper } from './AdvancedPlans/PlansGrid/GridWrapper';
import {
  PlansPageBottomBanner,
  websiteBottomBannerMap,
  enterpriseBottomBannerMap,
} from './BottomBanners';
import classNames from 'classnames';
import { PlansPageFooter } from './PlansPageFooter';
import { PlansPageBottomBannerProps } from './BottomBanners/PlansPageBottomBanner';
import { plansPageTabs } from '../../constants';

const PlansPageComp: React.FC<PlansPageProps> = (props) => {
  const controller = usePlansPageController(props);
  const { t } = useTranslation();
  const { theme: themeName } = usePageModeContext();
  const {
    features,
    businessFeatures,
    sections,
    websiteFeatureGroups,
    businessFeatureGroups,
  } = props.rootStore.appStore.appContext;
  const {
    planType,
    shouldShowPaymentBanner,
    shouldStoreIntentNoTabs,
    jumpToTab,
  } = props.rootStore.appStore;
  const {
    advancedPlansBusinessProducts,
    advancedPlansWebsiteProducts,
    shouldShowLimitedTimeBanner,
    websiteBannerCTA,
    isRequestACallModalOpened,
    closeRequestACallModal,
    onSelectProduct,
    hasEnterpriseProduct,
  } = controller;

  const handleTabChange = (index: number) => {
    const { selectTab } = props.rootStore.appStore;
    selectTab(plansPageTabs[index]);
  };

  const bottomBannerProps = (
    goToPlan: PlanType,
  ): Omit<PlansPageBottomBannerProps, 'cta'> => {
    const bottomBannerMap =
      goToPlan === PlanType.Website
        ? websiteBottomBannerMap
        : enterpriseBottomBannerMap;
    return hasEnterpriseProduct
      ? translateAllKeyInObject(bottomBannerMap[themeName], t)
      : translateAllKeyInObject(bottomBannerMap[Theme.Default], t);
  };

  const isEnterpriseTabEnabled = props.experiments.enabled(
    Experiments.AddEnterpriseTab,
  );

  return (
    <div
      data-testid='advanced-plans-page'
      className={classNames(s.root, {
        [s.enterprise]: planType === PlanType.Enterprise,
      })}
    >
      <Tabs
        onChange={handleTabChange}
        defaultIndex={plansPageTabs.indexOf(planType)}
        index={plansPageTabs.indexOf(planType)}
      >
        {!shouldStoreIntentNoTabs && (
          <GridWrapper>
            <TabButtons />
          </GridWrapper>
        )}
        <TabPanels>
          <TabPanelTransition predicate={planType === PlanType.Website}>
            <TabPanel data-testid='website-tab' className={s.tabPanel}>
              {planType === PlanType.Website && (
                <GridWrapper>
                  {shouldShowPaymentBanner && (
                    <CustomersPaymentBanner action={jumpToTab} />
                  )}
                  {shouldStoreIntentNoTabs && (
                    <NoTabsBanner action={jumpToTab} />
                  )}
                  <PlansGrid
                    onClickSelect={onSelectProduct}
                    features={features}
                    products={advancedPlansWebsiteProducts}
                    sections={[]}
                    groups={websiteFeatureGroups}
                  />
                </GridWrapper>
              )}
              {shouldStoreIntentNoTabs && isEnterpriseTabEnabled ? (
                <PlansPageBottomBanner
                  cta={() => websiteBannerCTA(PlanType.Enterprise)}
                  {...bottomBannerProps(PlanType.Enterprise)}
                />
              ) : (
                <PlansPageBottomBanner
                  cta={() => websiteBannerCTA(PlanType.Business)}
                  {...bottomBannerProps(PlanType.Website)}
                />
              )}
            </TabPanel>
          </TabPanelTransition>
          <TabPanelTransition predicate={planType === PlanType.Business}>
            <TabPanel data-testid='business-tab' className={s.tabPanel}>
              {planType === PlanType.Business && (
                <>
                  {shouldShowLimitedTimeBanner && <LimitedTimeBanner />}
                  {shouldStoreIntentNoTabs && (
                    <NoTabsBanner action={jumpToTab} />
                  )}
                  <GridWrapper>
                    <PlansGrid
                      onClickSelect={onSelectProduct}
                      features={businessFeatures ?? features}
                      products={advancedPlansBusinessProducts}
                      sections={sections}
                      groups={businessFeatureGroups}
                    />
                  </GridWrapper>
                  {shouldStoreIntentNoTabs && isEnterpriseTabEnabled && (
                    <PlansPageBottomBanner
                      cta={() => websiteBannerCTA(PlanType.Enterprise)}
                      {...bottomBannerProps(PlanType.Enterprise)}
                    />
                  )}
                </>
              )}
            </TabPanel>
          </TabPanelTransition>
          <TabPanelTransition predicate={planType === PlanType.Enterprise}>
            <TabPanel data-testid='enterprise-tab' className={s.tabPanel}>
              {planType === PlanType.Enterprise && <EnterpriseTab />}
            </TabPanel>
          </TabPanelTransition>
        </TabPanels>
      </Tabs>

      {planType !== PlanType.Enterprise && (
        <GridWrapper>
          <PlansPageFooter />
        </GridWrapper>
      )}
      <RequestCallModal
        onCancelClick={closeRequestACallModal}
        isOpen={isRequestACallModalOpened}
      />
    </div>
  );
};

export const PlansPage = withGtmLogger(
  withExperiments(withInjectedRootStore(observer(PlansPageComp))),
);
